import { useEffect, useState, type ComponentType } from "react";
import { Plug } from "lucide-react";
import { toast } from "sonner";
import { PageShell } from "@components/layout/PageShell";
import { PageHeader } from "@components/layout/PageHeader";
import { SidebarTrigger } from "@components/ui/sidebar";
import { Button } from "@components/ui/button";
import { Card, CardContent } from "@components/ui/card";
import { IntegrationCatalog } from "@components/integrations/IntegrationCatalog";
import { JiraConnectionCard } from "@components/integrations/JiraConnectionCard";
import { ShortcutConnectionCard } from "@components/integrations/ShortcutConnectionCard";
import { MondayConnectionCard } from "@components/integrations/MondayConnectionCard";
import { LinearConnectionCard } from "@components/integrations/LinearConnectionCard";
import { GitHubConnectionCard } from "@components/integrations/GitHubConnectionCard";
import { ZendeskConnectionCard } from "@components/integrations/ZendeskConnectionCard";
import { HubSpotConnectionCard } from "@components/integrations/HubSpotConnectionCard";
import { IntercomConnectionCard } from "@components/integrations/IntercomConnectionCard";
import { SalesforceConnectionCard } from "@components/integrations/SalesforceConnectionCard";
import { SlackConnectionCard } from "@components/integrations/SlackConnectionCard";
import { IntegrationSetupSheet } from "@components/integrations/IntegrationSetupSheet";
import { AzureDevOpsConnectionCard } from "@components/integrations/AzureDevOpsConnectionCard";
import type { ConnectorManifest, IntegrationProvider } from "@io-spoke/contracts/integrations";
import { listIntegrationConnections, type IntegrationConnection } from "../lib/integrations";
import { ConnectedIntegrationCard } from "@components/integrations/ConnectedIntegrationCard";
import { setupCopy } from "@components/integrations/integrationPresentation";

type Props = { navigate: (route: string) => void };

type ConnectionFormProps = { connection?: IntegrationConnection; onSaved: () => Promise<void>; onCancel: () => void };
const connectionForms: Record<IntegrationProvider, ComponentType<ConnectionFormProps>> = {
  azure_devops: AzureDevOpsConnectionCard, jira: JiraConnectionCard, shortcut: ShortcutConnectionCard,
  monday: MondayConnectionCard, linear: LinearConnectionCard, github: GitHubConnectionCard,
  zendesk: ZendeskConnectionCard, hubspot: HubSpotConnectionCard, intercom: IntercomConnectionCard,
  salesforce: SalesforceConnectionCard, slack: SlackConnectionCard,
};

export function IntegrationsPage({ navigate }: Props) {
  const [connections, setConnections] = useState<IntegrationConnection[]>([]);
  const [editingConnection, setEditingConnection] = useState<IntegrationConnection>();
  const [setupProvider, setSetupProvider] = useState<IntegrationProvider | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setConnections(await listIntegrationConnections()); }
    catch (error) { toast.error(error instanceof Error ? error.message : "Unable to load integrations."); }
    finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, []);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const jira = params.get("jira"); const zendesk = params.get("zendesk"); const hubspot = params.get("hubspot"); const intercom = params.get("intercom"); const salesforce = params.get("salesforce"); const slack = params.get("slack");
    if (!jira && !zendesk && !hubspot && !intercom && !salesforce && !slack) return;
    if (jira === "connected") toast.success("Jira connected securely through Atlassian.");
    else if (jira) toast.error("Jira authorization was not completed. Please try again.");
    if (zendesk === "connected") toast.success("Zendesk connected securely.");
    else if (zendesk) toast.error("Zendesk authorization was not completed. Please try again.");
    if (hubspot === "connected") toast.success("HubSpot connected securely."); else if (hubspot) toast.error("HubSpot authorization was not completed. Please try again.");
    if (intercom === "connected") toast.success("Intercom connected securely."); else if (intercom) toast.error("Intercom authorization was not completed. Please try again.");
    if (salesforce === "connected") toast.success("Salesforce connected securely."); else if (salesforce) toast.error("Salesforce authorization was not completed. Please try again.");
    if (slack === "connected") toast.success("Slack connected securely."); else if (slack) toast.error("Slack authorization was not completed. Invite IO Spoke to the channel and try again.");
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const closeSetup = () => {
    setSetupProvider(null);
    setEditingConnection(undefined);
  };

  const edit = (connection: IntegrationConnection) => {
    setEditingConnection(connection);
    setSetupProvider(connection.provider);
  };
  const selectCatalogConnector = (connector: ConnectorManifest) => {
    if (connector.id === "io_spoke_demo") return;
    setSetupProvider(connector.id);
  };

  const ConnectionForm = setupProvider ? connectionForms[setupProvider] : null;

  return <PageShell>
    <PageHeader leading={<SidebarTrigger />} eyebrow="Workspace settings" title="Integrations" description="Connect IO Spoke to the tools where your teams execute work." actions={<Button variant="outline" onClick={() => navigate("/settings")}>Settings</Button>} />
    <div className="mt-8"><IntegrationCatalog onConfigure={selectCatalogConnector} /></div>
    <IntegrationSetupSheet open={setupProvider !== null} onOpenChange={(open) => { if (!open) closeSetup(); }}
      title={setupProvider ? `${editingConnection ? "Reconfigure" : "Connect"} ${setupCopy[setupProvider].name}` : "Connect integration"}
      description={setupProvider ? setupCopy[setupProvider].description : "Choose an integration to continue."}>
      {ConnectionForm ? <ConnectionForm connection={editingConnection} onCancel={closeSetup} onSaved={async () => { await load(); closeSetup(); }} /> : null}
    </IntegrationSetupSheet>
    <section className="mt-8">
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">Connected tools</h2>
        {loading ? <p className="text-sm text-muted-foreground">Loading integrations…</p> : null}
        {!loading && connections.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center">
              <Plug className="mx-auto mb-3 h-8 w-8 text-muted-foreground" aria-hidden="true" />
              <p className="font-medium">No integrations connected</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Choose an available integration above to begin synchronizing evidence.
              </p>
            </CardContent>
          </Card>
        ) : null}
        {!loading
          ? connections.map((connection) => (
              <ConnectedIntegrationCard
                key={connection.id}
                connection={connection}
                onEdit={edit}
                onReload={load}
                onOpenObligations={() => navigate("/obligations")}
              />
            ))
          : null}
      </div>
    </section>
  </PageShell>;
}
